"use client"
import { Badge } from "@/app/(site)/_components/Molecules/Badge"
import GCBadge from "@/app/(site)/_components/Molecules/GCBadge"
import SanityBadgeList from "@/app/(site)/_components/Molecules/GCBadge/SanityBadgeList"
import { ProductCardContext } from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardContext"
import ProductCardDetails from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardDetails"
import ProductCardDetailsThumbnailType from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardDetailsThumbnailType"
import ProductImageSlider from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductImageSlider"
import { type MediaGroupContentType } from "@/gc-sanity-types"
import { getSortedColorSwatches } from "@/lib/content-service/helpers/transformProductData"
import {
    type ProductData,
    type RelatedVariant
} from "@/lib/content-service/types/ContentServiceProduct"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { resolveSanityInjectBadges } from "@/src/utils/badges/resolveSanityInjectBadges"
import { clsx } from "@/src/utils/clsx"
import { getProductVariantLinkLabel } from "@/src/utils/product/getProductVariantLinkLabel"
import { getLangCode } from "@/src/utils/string/getLangCode"
import placeholder from "@assets/images/placeholder.png"
import { useMemo, useState } from "react"

const placeholderUrl = placeholder.src

export type VariationsDisplayType = "swatches" | "thumbnails"

type ProductCardProps = {
    product: ProductData
    className?: string
    index?: number
    isAboveTheFold?: boolean
    listHierarchy?: string
    listId?: string
    listName?: string
    listType?: string
    mediaRatio?: string
    imageSizes?: string
    params: { locale: string }
    showBenefits?: boolean
    variationsDisplay?: VariationsDisplayType
    showSalePercentageBadge?: boolean
    related?: "relatedProducts" | "relatedVariants"
    mediaOverride?: MediaGroupContentType
}

export default function ProductCard({
    product,
    className,
    index,
    isAboveTheFold = false,
    listHierarchy,
    listId = "plp",
    listName = "Product List",
    listType = "plp",
    mediaRatio = "0.7",
    imageSizes,
    params,
    showBenefits,
    variationsDisplay = "swatches",
    showSalePercentageBadge = false,
    related = "relatedProducts",
    mediaOverride
}: ProductCardProps) {
    const langCode = getLangCode(params.locale) || "en"
    const siteContext = useSiteContext()

    const relatedVariants = product[related] || []
    const allVariants: RelatedVariant[] = [product, ...relatedVariants]

    const [currentVariant, setCurrentVariant] = useState<ProductData | RelatedVariant>(
        product
    )

    const [thumbnailSelected, setThumbnailSelected] = useState<number | undefined>(
        undefined
    )

    const {
        name,
        price,
        priceAsNumber,
        priceBeforeDiscount,
        priceBeforeDiscountAsNumber,
        showAsOnSale,
        uri,
        benefitList,
        media,
        combinedBadges,
        sanity_inject_badge_json,
        available
    } = currentVariant || {}

    const sanityBadges = resolveSanityInjectBadges(
        sanity_inject_badge_json,
        langCode,
        params.locale
    )

    const sortedSwatches = getSortedColorSwatches(allVariants)
    const variantLinkLabel = getProductVariantLinkLabel(
        langCode,
        name ?? "",
        currentVariant.color_swatch?.color_name
    )

    const hasMedia = Array.isArray(media) && media.length > 0
    const images = useMemo(
        () => (hasMedia ? media.map(m => m.url) : [placeholderUrl]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [media]
    )

    const getSalePercentageBadge = () => {
        const percentage = Math.round(
            (1 - (priceAsNumber ?? 0) / (priceBeforeDiscountAsNumber ?? 1)) * 100
        )
        return { asNumber: percentage, asString: `-${percentage}%` }
    }

    const salePercentageBadge = getSalePercentageBadge()

    const combinedBadgesWithSalePercentage = [
        ...(combinedBadges
            ?.filter(
                badge =>
                    badge &&
                    typeof badge === "string" &&
                    !badge.toLowerCase().includes("sale")
            )
            .map(badge => ({
                text: badge,
                type: "information" as const
            })) || []),
        salePercentageBadge.asNumber > 0
            ? { text: salePercentageBadge.asString, type: "sale" as const }
            : undefined
    ].filter(Boolean)

    const combinedBadgesWithSalePercentageExists =
        combinedBadgesWithSalePercentage.length > 0

    const handleChangeVariant = (relatedVariant?: RelatedVariant | undefined) => {
        if (relatedVariant) {
            setCurrentVariant(prevState => ({ ...prevState, ...relatedVariant }))
        } else if (thumbnailSelected) {
            setCurrentVariant(allVariants[thumbnailSelected])
        } else {
            setCurrentVariant(product)
        }
    }

    const handleProductClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const productIndex = (e.currentTarget as HTMLElement)
            .closest("[data-product-index]")
            ?.getAttribute("data-product-index")

        if (productIndex) {
            index = parseInt(productIndex)
        }

        const productToTrack = currentVariant !== product ? currentVariant : product

        import("@/lib/utils/tracking/gtmEvents").then(({ selectItemEvent }) => {
            selectItemEvent(
                productToTrack,
                { size: undefined, index },
                listName,
                listId,
                siteContext,
                listType,
                listHierarchy
            )
        })
    }

    return (
        <ProductCardContext.Provider
            value={{
                currentVariant,
                setCurrentVariant,
                thumbnailSelected,
                setThumbnailSelected,
                handleChangeVariant
            }}
        >
            <div
                className={clsx(
                    "group relative isolate flex flex-col gap-8 sm:gap-12",
                    className
                )}
                data-search-result-id={product.search_result_id}
                data-product-listing-result-id={product.product_listing_result_id}
                data-recommendation-id={product.recommendation_id}
            >
                <div className="relative">
                    {sanityBadges.length > 0 ? (
                        <SanityBadgeList
                            badges={sanityBadges}
                            className="absolute left-0 top-0 z-10 m-8 flex flex-wrap gap-4 group-data-[desktop-cols='5']/grid:hidden group-data-[desktop-cols='6']/grid:hidden group-data-[mobile-cols='3']/grid:hidden"
                        >
                            {showSalePercentageBadge &&
                                salePercentageBadge.asNumber > 0 && (
                                    <GCBadge
                                        textColor="text-main-black"
                                        badgeText={salePercentageBadge.asString}
                                        badgeType="sale"
                                    />
                                )}
                        </SanityBadgeList>
                    ) : (
                        (!!combinedBadges?.length ||
                            (showSalePercentageBadge && showAsOnSale)) && (
                            <div className="absolute left-0 top-0 z-10 flex flex-wrap gap-2 group-data-[desktop-cols='5']/grid:hidden group-data-[desktop-cols='6']/grid:hidden group-data-[mobile-cols='3']/grid:hidden">
                                {combinedBadges &&
                                    !showSalePercentageBadge &&
                                    combinedBadges?.map((badge, idx) => (
                                        <Badge key={idx} badge={badge} />
                                    ))}
                                {combinedBadgesWithSalePercentageExists && (
                                    <div className="m-8 flex flex-wrap gap-4">
                                        {showSalePercentageBadge &&
                                            combinedBadgesWithSalePercentage.map(
                                                badge => {
                                                    if (!badge) return null
                                                    return (
                                                        <GCBadge
                                                            key={badge.text}
                                                            textColor={
                                                                badge.type === "sale"
                                                                    ? "text-main-black"
                                                                    : "text-main-white"
                                                            }
                                                            badgeText={badge.text}
                                                            badgeType={badge.type}
                                                        />
                                                    )
                                                }
                                            )}
                                    </div>
                                )}
                            </div>
                        )
                    )}
                    <ProductImageSlider
                        images={images}
                        altText={variantLinkLabel}
                        isAboveTheFold={isAboveTheFold}
                        aspectRatio={mediaRatio}
                        imageSizes={imageSizes}
                        imageHref={`/product/${uri}`}
                        onImageClick={handleProductClick}
                        firstImageOverride={
                            currentVariant === product
                                ? mediaOverride?.imageGroup?.images[0]?.image.asset.url
                                : undefined
                        }
                    />
                </div>

                {variationsDisplay === "swatches" ? (
                    <ProductCardDetails
                        sortedSwatches={sortedSwatches}
                        langCode={langCode}
                        uri={uri}
                        variantLinkLabel={variantLinkLabel}
                        name={name}
                        price={price}
                        priceBeforeDiscount={priceBeforeDiscount}
                        showAsOnSale={showAsOnSale}
                        productBenefits={benefitList}
                        available={available}
                        showBenefits={showBenefits}
                        listName={listName}
                        listId={listId}
                        listType={listType}
                        listHierarchy={listHierarchy}
                    />
                ) : (
                    <ProductCardDetailsThumbnailType
                        allVariants={allVariants}
                        langCode={langCode}
                        isAboveTheFold={isAboveTheFold}
                        uri={uri}
                        variantLinkLabel={variantLinkLabel}
                        name={name}
                        price={price}
                        priceBeforeDiscount={priceBeforeDiscount}
                        showAsOnSale={showAsOnSale}
                        showBenefits={showBenefits}
                        listName={listName}
                        listId={listId}
                        listType={listType}
                        listHierarchy={listHierarchy}
                    />
                )}
            </div>
        </ProductCardContext.Provider>
    )
}
